import React, { useState, useEffect } from "react";
import { Card, Col, Row, Typography, Input, Button, Alert, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { OverviewWrapper } from "./PartnerOverview.style";
import {
  SearchOutlined,
  DollarOutlined,
  TeamOutlined,
  FileTextOutlined,
  UserAddOutlined,
  RightOutlined,
  UsergroupAddOutlined,
  RocketOutlined,
  PhoneOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Title, Text, Paragraph } = Typography;

const formatCurrency = (value) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return `₹${value?.toLocaleString('en-IN') || 0}`;
};

const PartnerOverview = () => {
  const [profileCompletion, setProfileCompletion] = useState({ isComplete: true, completionPercentage: 100 });
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalEarnings: 0,
    benchPoolSize: 0,
    jobsApplied: 0,
    talentsHired: 0,
  });
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const checkProfileCompletion = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(API_CONST.CHECK_PROFILE_COMPLETION, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setProfileCompletion(response.data);
        }
      } catch (error) {
        console.error("Error checking profile completion:", error);
      }
    };

    const fetchDashboardMetrics = async () => {
      setMetricsLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          `${API_CONST.BASE_URL}/partner-dashboard-metrics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          setDashboardMetrics(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
      } finally {
        setMetricsLoading(false);
      }
    };

    checkProfileCompletion();
    fetchDashboardMetrics();
  }, []);

  const metricsData = [
    { count: formatCurrency(dashboardMetrics.totalEarnings), label: "Total Earning", icon: <DollarOutlined /> },
    { count: dashboardMetrics.benchPoolSize, label: "Bench Pool", icon: <TeamOutlined /> },
    { count: dashboardMetrics.jobsApplied, label: "Jobs Applied", icon: <FileTextOutlined /> },
    { count: dashboardMetrics.talentsHired, label: "Talents Hired", icon: <UserAddOutlined /> },
  ];

  const navigate = useNavigate();

  const handleSearch = () => {
    if (searchValue.trim()) {
      navigate(`/partner/ongoing-jobs?search=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const quickActions = [
    {
      icon: <UsergroupAddOutlined />,
      title: "Add your Bench pool",
      description: "Post job briefs for your required roles and start receiving recommends & applications.",
      action: "Add Bench Pool",
      onClick: () => navigate("/partner/bench-pool"),
    },
    {
      icon: <RocketOutlined />,
      title: "Deploy your Bench Resources",
      description: "Let's online your project — and start building your development team around its objectives.",
      action: "Initiate a Contract",
      onClick: () => navigate("/partner/ongoing-jobs"),
    },
    {
      icon: <PhoneOutlined />,
      title: "Free Consultation to apply in Jobs",
      description: "It is a long established fact that a reader will be distracted by the readable content of a page when looking at its.",
      action: "Book a Meeting for FREE",
      onClick: () => console.log("Free Consultation"),
    },
  ];

  return (
    <OverviewWrapper>
      <Row align="middle" justify="space-between" style={{ marginBottom: "20px" }}>
        <Col>
          <Title level={2} className="page-title">Dashboard</Title>
        </Col>
      </Row>

      {!profileCompletion.isComplete && (
        <Alert
          message={
            <span>
              {profileCompletion.completionPercentage}% Your account Completed. Please complete your full account setup.{" "}
              <Button type="link" style={{ padding: 0 }} onClick={() => navigate("/partner/company-details")}>
                Complete Company Profile
              </Button>
            </span>
          }
          type="warning"
          showIcon
          closable
          style={{ marginBottom: "20px" }}
          className="account-alert"
        />
      )}

      <Spin spinning={metricsLoading}>
        <Row gutter={16} style={{ marginBottom: "30px" }}>
          {metricsData.map((item, index) => (
            <Col key={index} xs={12} sm={12} md={6} lg={6}>
              <Card className="metric-card">
                <div className="metric-content">
                  <div className="metric-icon">{item.icon}</div>
                  <div>
                    <Title level={2} className="metric-count">{item.count}</Title>
                    <Text className="metric-label">{item.label}</Text>
                  </div>
                  <RightOutlined className="arrow-icon" />
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>

      <div className="hero-section">
        <Title level={3} className="hero-title">
          Explore world-class <span className="highlight">Opportunities</span> and{" "}
          <span className="highlight">Requirements</span> from Global Top Companies
        </Title>
        <Input
          size="large"
          placeholder="Search by skills or role e.g. Java, AWS, UI Designers etc."
          prefix={<SearchOutlined />}
          className="hero-search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onPressEnter={handleSearch}
        />
      </div>

      <div className="quick-action-section">
        <Title level={4} className="section-title">Quick action</Title>
        <Row gutter={24}>
          {quickActions.map((action, index) => (
            <Col key={index} xs={24} sm={24} md={8}>
              <Card className="action-card">
                <div className="action-icon-wrapper">
                  {action.icon}
                </div>
                <Title level={5} className="action-title">{action.title}</Title>
                <Paragraph className="action-description">{action.description}</Paragraph>
                <Button 
                  type="link" 
                  className="action-link"
                  onClick={action.onClick}
                >
                  {action.action} <ArrowRightOutlined />
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <div className="about-section">
        <Title level={4} className="section-title">About HireNXT</Title>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Card className="about-card">
              <Title level={5}>Want us to help you with Requirements?</Title>
              <Paragraph>
                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has 
                been the industry's standard dummy text ever since the 1500s, when an unknown printer took 
                a galley of type and scrambled it to make a type specimen book.
              </Paragraph>
              <Paragraph className="tour-text">
                Take a tour of HireNXT by looking at walkthrough of entire product.
              </Paragraph>
              <Button type="link" className="about-link">
                Book a Consultation for FREE <ArrowRightOutlined />
              </Button>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="about-card">
              <Title level={5}>Schedule a Product tour</Title>
              <Paragraph>
                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has 
                been the industry's standard dummy text ever since the 1500s, when an unknown printer took 
                a galley of type and scrambled it to make a type specimen book.
              </Paragraph>
              <Paragraph className="tour-text">
                Take a tour of HireNXT by looking at walkthrough of entire product.
              </Paragraph>
              <Button type="link" className="about-link">
                Book a Product tour <ArrowRightOutlined />
              </Button>
            </Card>
          </Col>
        </Row>
      </div>
    </OverviewWrapper>
  );
};

export default PartnerOverview;
